type FormulaPart = {
  type: "chip" | "text";
  value: string;
  key: string;
};

type ParenMatch = {
  start: number;
  end: number;
  value: string;
};

export const parseFormula = (formulaStr: string, tokenList: string[]): FormulaPart[] => {
  const parts: FormulaPart[] = [];
  let currentIndex = 0;

  // Create a set of tokens for quick lookup
  const tokenSet = new Set(tokenList);

  // Regular expression to match parenthetical expressions
  const parenRegex = /\([^()]+\)/g;

  // Regular expression to match token with number operations (e.g., A+10, B*0.015, B*-1, A*-1, B * -1, A*1024*1024)
  const tokenNumberRegex = new RegExp(`(${tokenList.join("|")})(?:\\s*[+\\-*/]\\s*-?\\d+(?:\\.\\d+)?)+`, "g");

  // Find all single token + number expressions
  const tokenNumberMatches: ParenMatch[] = [];
  let tokenMatch: RegExpExecArray | null;
  while ((tokenMatch = tokenNumberRegex.exec(formulaStr)) !== null) {
    tokenNumberMatches.push({
      start: tokenMatch.index,
      end: tokenMatch.index + tokenMatch[0].length,
      value: tokenMatch[0],
    });
  }

  // Find all parenthetical expressions and check if they contain only tokens, operators, and numbers
  const parenMatches: ParenMatch[] = [];
  let match: RegExpExecArray | null;
  while ((match = parenRegex.exec(formulaStr)) !== null) {
    const content = match[0].slice(1, -1); // Remove parentheses
    // Check if this parenthetical expression contains only tokens, operators, and numbers
    const cleanedContent = content.replace(/[+\-*/\s\d.]/g, "");
    const chars = cleanedContent.split("").filter((char) => char !== "");

    // If all remaining characters are valid tokens, this is a tokenizable expression
    if (chars.length > 0 && chars.every((char) => tokenSet.has(char))) {
      parenMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        value: match[0],
      });
    }
  }

  // Combine and sort all matches by start position
  const allMatches: ParenMatch[] = [...parenMatches, ...tokenNumberMatches].sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the longer one)
  const nonOverlappingMatches: ParenMatch[] = [];
  for (let i = 0; i < allMatches.length; i++) {
    const current = allMatches[i];
    let isOverlapping = false;

    for (let j = 0; j < nonOverlappingMatches.length; j++) {
      const existing = nonOverlappingMatches[j];
      if (
        (current.start >= existing.start && current.start < existing.end) ||
        (current.end > existing.start && current.end <= existing.end)
      ) {
        isOverlapping = true;
        break;
      }
    }

    if (!isOverlapping) {
      nonOverlappingMatches.push(current);
    }
  }

  while (currentIndex < formulaStr.length) {
    let handled = false;

    // Check if we're at the start of any match
    const matchAtPosition: ParenMatch | undefined = nonOverlappingMatches.find((m) => m.start === currentIndex);
    if (matchAtPosition) {
      parts.push({
        type: "chip",
        value: matchAtPosition.value,
        key: `match-${currentIndex}`,
      });
      currentIndex = matchAtPosition.end;
      handled = true;
    }

    // If not handled by any match, check for single token
    if (!handled) {
      const currentChar: string = formulaStr[currentIndex];

      if (tokenSet.has(currentChar)) {
        // Check if this token is part of a token+number expression
        let isPartOfTokenNumber = false;
        for (const match of tokenNumberMatches) {
          if (currentIndex >= match.start && currentIndex < match.end) {
            isPartOfTokenNumber = true;
            break;
          }
        }

        if (!isPartOfTokenNumber) {
          // Check if it's a standalone token
          const prevChar: string = currentIndex > 0 ? formulaStr[currentIndex - 1] : "";
          const nextChar: string = currentIndex + 1 < formulaStr.length ? formulaStr[currentIndex + 1] : "";

          // Consider it a token if it's not surrounded by letters
          const isStandalone: boolean = !/[a-zA-Z]/.test(prevChar) && !/[a-zA-Z]/.test(nextChar);

          if (isStandalone) {
            parts.push({
              type: "chip",
              value: currentChar,
              key: `token-${currentIndex}`,
            });
            currentIndex++;
            handled = true;
          }
        }
      }
    }

    // If still not handled, add as text
    if (!handled) {
      if (parts.length === 0 || parts[parts.length - 1].type !== "text") {
        parts.push({
          type: "text",
          value: formulaStr[currentIndex],
          key: `text-${currentIndex}`,
        });
      } else {
        parts[parts.length - 1].value += formulaStr[currentIndex];
      }
      currentIndex++;
    }
  }

  return parts;
};

export enum MetricPathType {
  CREATE = "create",
  LIST = "list",
  VIEW = "view",
}

export const getMetricPath = (customerId: string, pathType: MetricPathType, metricId?: string) => {
  const basePath = `/customers/${customerId}/operate/metrics`;

  switch (pathType) {
    case MetricPathType.CREATE:
      return `${basePath}/create`;
    case MetricPathType.LIST:
      return basePath;
    case MetricPathType.VIEW:
      if (!metricId) {
        return basePath;
      }
      return `${basePath}/${metricId}`;
    default:
      return basePath;
  }
};
